import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Users, Upload, MessageSquare, Eye, BarChart3, RefreshCw, Settings, ChevronRight, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';

const GUIDES = [
  { id: 'upload', icon: Users, title: 'Upload & Add Participants', subtitle: 'CSV import for staff and youth', role: 'admin',
    steps: [
      { title: 'Upload Staff First', detail: 'Prepare a CSV with columns: name, email, role. Set role as "lead_monitor" for mentors/counselors. Upload via Admin Dashboard → Users → Import CSV.' },
      { title: 'Upload Youth', detail: 'Prepare a CSV with: name, email, role (youth), cohort, lead_monitor_email. The system auto-assigns each youth to their mentor based on the email match.' },
      { title: 'Welcome Emails', detail: 'Everyone receives a welcome email with their login credentials. They log in and change their password on first login.' },
      { title: 'Verify', detail: 'Check the Users tab — all accounts should appear with their roles, cohorts, and assignments.' }
    ]},
  { id: 'files', icon: Upload, title: 'Upload Files & Documents', subtitle: 'Add resources for your youth', role: 'admin',
    steps: [
      { title: 'Go to Resources', detail: 'From your dashboard, navigate to the Resources tab or section.' },
      { title: 'Choose Upload Type', detail: 'Upload File (PDFs, images, max 10MB) or Add Link (external URLs, Google Docs).' },
      { title: 'Fill Details', detail: 'Add a title, description, and category. Toggle "Featured" to pin it at the top.' },
      { title: 'Youth Access', detail: 'Youth see resources on their Resources page, organized by category.' }
    ]},
  { id: 'messages', icon: MessageSquare, title: 'View & Respond to Messages', subtitle: 'Youth and parent communication', role: 'staff',
    steps: [
      { title: 'Check Messages Tab', detail: 'Your dashboard has a Messages tab showing parent messages and group messages.' },
      { title: 'Reply to Parents', detail: 'Click a parent thread → type your reply → click Reply. Parents get an email notification.' },
      { title: 'Message All Youth', detail: 'Click "Message All" in the Activity Feed. Toggle "Must Read" for critical announcements.' },
      { title: 'Nudge Groups', detail: 'Filter youth (No Courses, Stalled, etc.) → click "Nudge This Group" to message only that filtered set.' }
    ]},
  { id: 'activity', icon: Eye, title: 'View Youth Activity & Profiles', subtitle: 'Track development and progress', role: 'staff',
    steps: [
      { title: 'Youth Cards', detail: 'My Youth tab shows cards with name, interests, school, career goals, workshop progress, and risk flags.' },
      { title: 'Timeline', detail: 'Click any youth card to open their full Development Timeline — every milestone in chronological order.' },
      { title: 'AI Report', detail: 'In the timeline, click "Generate AI Profile Report" for a comprehensive analysis of strengths, growth areas, and recommendations.' },
      { title: 'Filters', detail: 'Use the filter bar: All, Needs Attention, No Courses, Stalled — to quickly find youth who need your help.' }
    ]},
  { id: 'staff', icon: Settings, title: 'Monitor Staff Activity', subtitle: 'For organization administrators', role: 'admin',
    steps: [
      { title: 'Staff Tab', detail: 'View all lead monitors/mentors in your program — see their assigned youth count and activity.' },
      { title: 'Staff Dashboard View', detail: 'Navigate to /coach to see the same unified Staff Dashboard your mentors see.' },
      { title: 'Evaluate Performance', detail: 'Look for: parent response times, youth engagement rates, workshop progress among their assigned youth.' },
      { title: 'Privacy', detail: 'Private DMs between youth and mentor are NOT visible to admins — this protects trust.' }
    ]},
  { id: 'reports', icon: BarChart3, title: 'Create & Run Reports', subtitle: 'Analytics and data export', role: 'admin',
    steps: [
      { title: 'Reports Tab', detail: 'Your Admin Dashboard has a Reports tab with program KPIs: total youth, goals, courses, check-ins, wins.' },
      { title: 'Youth Table', detail: 'Click any youth name in the table for their full individual report with AI summary.' },
      { title: 'Export CSV', detail: 'Click "Export CSV" to download all youth data as a spreadsheet for funder reports and presentations.' },
      { title: 'AI Reports', detail: 'Generate individual AI Profile Reports from the Staff Dashboard → youth timeline.' }
    ]},
  { id: 'program', icon: RefreshCw, title: 'End & Start Programs', subtitle: 'Managing program seasons', role: 'admin',
    steps: [
      { title: 'Generate Final Reports', detail: 'Export CSV and generate AI reports before ending the program.' },
      { title: 'Archive Program', detail: 'Contact YAS Connect to archive the current season. All data is preserved but youth can no longer submit new activity.' },
      { title: 'Start New Season', detail: 'Your admin account stays the same. Upload new participant lists, create new events, upload new resources.' },
      { title: 'Billing', detail: 'Platform access is per organization, not per program. Active subscription = unlimited program cycles.' }
    ]},
  { id: 'essential', icon: BookOpen, title: 'Essential Daily Processes', subtitle: 'What to do every day/week', role: 'all',
    steps: [
      { title: 'Daily (Staff)', detail: 'Check Activity Feed → respond to messages → review youth risk flags → follow up with at-risk youth.' },
      { title: 'Weekly (Staff)', detail: 'Review Courses tab → nudge stalled youth → generate AI reports for upcoming meetings.' },
      { title: 'Before Meetings', detail: 'Click youth card → view timeline → generate AI report → walk in prepared.' },
      { title: 'Quarterly (Admin)', detail: 'Export full CSV → review program KPIs → generate comprehensive reports for funders.' }
    ]}
];

const HelpGuidesPage = ({ user }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);
  const isAdmin = ['admin', 'program_admin', 'platform_admin'].includes(user?.role);
  const isStaff = ['lead_monitor', 'coach', 'admin', 'program_admin', 'platform_admin'].includes(user?.role);

  const filteredGuides = GUIDES.filter(g => g.role === 'all' || (g.role === 'admin' && isAdmin) || (g.role === 'staff' && isStaff));

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="pb-8 pt-4 px-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div>
              <h1 className="text-xl font-black text-white">Help & Guides</h1>
              <p className="text-xs text-white/50">Step-by-step walk-throughs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4 space-y-3 pb-24">
        {filteredGuides.map((guide, i) => (
          <motion.div key={guide.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="rounded-2xl overflow-hidden border border-gray-100" data-testid={`guide-${guide.id}`}>
              <button onClick={() => setExpanded(expanded === guide.id ? null : guide.id)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition-all">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <guide.icon className="w-5 h-5 text-orange-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">{guide.title}</p>
                  <p className="text-[10px] text-gray-400">{guide.subtitle}</p>
                </div>
                {expanded === guide.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>
              {expanded === guide.id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="px-4 pb-4">
                  <div className="space-y-3 border-t pt-3">
                    {guide.steps.map((step, si) => (
                      <div key={si} className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{si + 1}</div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">{step.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{step.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default HelpGuidesPage;
